import React, { useEffect, useState } from 'react';
import { Plus, ShieldCheck, KeyRound, Trash2, RefreshCw, Copy, X, RotateCcw, Ban, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { authApi } from '../lib/authApi';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function AdminAccessCodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', notes: '', expires_days: 90, role: 'user' });
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all'); // all | active | revoked

  const reload = async () => {
    setLoading(true);
    try {
      const r = await authApi.listAccessCodes();
      setCodes(r.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!form.email.trim()) { setToast('Email required'); return; }
    setCreating(true);
    try {
      const r = await authApi.createAccessCode({
        email: form.email.trim(),
        name: form.name.trim() || undefined,
        notes: form.notes.trim() || undefined,
        expires_days: form.expires_days || undefined,
        role: form.role,
      });
      setToast(`Code ${r.code} created for ${r.email}`);
      setForm({ email: '', name: '', notes: '', expires_days: 90, role: 'user' });
      await reload();
    } catch (e) { setToast(e.response?.data?.detail || 'Could not create code'); }
    finally { setCreating(false); }
  };

  const regenerate = async (id) => {
    if (!window.confirm('Generate a NEW code for this user? The old code stops working.')) return;
    try {
      const r = await authApi.updateAccessCode(id, { regenerate: true });
      setToast(`New code: ${r.code}`);
      await reload();
    } catch (e) { setToast(e.response?.data?.detail || 'Regen failed'); }
  };

  const toggleRevoke = async (c) => {
    try {
      await authApi.updateAccessCode(c.id, { revoked: !c.revoked });
      await reload();
    } catch (e) { setToast(e.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Permanently delete code for ${c.email}?`)) return;
    try { await authApi.deleteAccessCode(c.id); await reload(); }
    catch (e) { setToast(e.response?.data?.detail || 'Delete failed'); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setToast(`Copied ${text}`); } catch { /* noop */ }
  };

  const visible = codes.filter((c) => filter === 'all' || (filter === 'active' ? !c.revoked : c.revoked));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Admin · Access codes' }]} />
        <div className="mt-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <ShieldCheck size={12} className="text-[#4A7C5E]" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Admin</span>
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight">Access codes</h1>
            <p className="mt-2 text-[13.5px] text-[#3A4A44] max-w-2xl">
              Create a 4-digit code for each person you want to let into the site. Share the code with them any way you like
              (text, email, Signal). They sign in with their email + code — no password needed.
            </p>
          </div>
          <button onClick={reload} className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md border border-[#D4C9AF] bg-white text-[12.5px] font-medium hover:border-[#4A7C5E]" data-testid="codes-reload">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {toast && (
          <div className="mt-4 rounded-md bg-[#F5EFDD] border border-[#D4C9AF] px-3.5 py-2.5 text-[12.5px] text-[#3A4A44] flex items-center justify-between" data-testid="codes-toast">
            <span>{toast}</span>
            <button onClick={() => setToast(null)} className="text-[#7A857F] hover:text-[#1A2622]"><X size={13} /></button>
          </div>
        )}

        <section className="mt-8 rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid="create-code-card">
          <h2 className="font-display text-lg font-semibold text-[#1A2622] flex items-center gap-2">
            <KeyRound size={15} className="text-[#A06628]" /> New access code
          </h2>
          <p className="mt-1 text-[12px] text-[#7A857F]">A 4-digit code is auto-generated. Share it manually with the user.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Field label="Email" required>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="person@example.com" className={inputCls} data-testid="code-email" />
            </Field>
            <Field label="Name (optional)">
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe" className={inputCls} data-testid="code-name" />
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="code-role">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Days valid">
              <input type="number" min={1} max={365} value={form.expires_days}
                onChange={(e) => setForm({ ...form, expires_days: parseInt(e.target.value) || 90 })}
                className={inputCls} data-testid="code-days" />
            </Field>
            <Field label="Notes (optional)">
              <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. physician partner" className={inputCls} data-testid="code-notes" />
            </Field>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={create} disabled={creating}
              className="h-10 px-4 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[13px] font-medium hover:bg-[#2D5F4F] inline-flex items-center gap-1.5"
              data-testid="code-create">
              <Plus size={13} /> {creating ? 'Creating…' : 'Create code'}
            </button>
          </div>
        </section>

        <section className="mt-8 rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid="codes-list-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display text-lg font-semibold text-[#1A2622]">Issued codes</h2>
              <p className="mt-1 text-[12px] text-[#7A857F]">{loading ? 'Loading…' : `${visible.length} of ${codes.length} shown`}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {['all', 'active', 'revoked'].map((s) => (
                <button key={s} onClick={() => setFilter(s)}
                  className={`h-8 px-3 rounded-md text-[12px] font-medium border capitalize ${filter === s ? 'bg-[#1A2622] text-[#FBF6EC] border-[#1A2622]' : 'bg-white text-[#3A4A44] border-[#D4C9AF] hover:border-[#4A7C5E]'}`}
                  data-testid={`codes-filter-${s}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="mt-4 -mx-5 overflow-x-auto">
            <table className="w-full text-[13px] min-w-[720px]">
              <thead className="text-[10.5px] uppercase tracking-wider text-[#7A857F] bg-[#F5EFDD]">                <tr>
                  <th className="text-left px-5 py-2">Email</th>
                  <th className="text-left px-5 py-2">Name</th>
                  <th className="text-left px-5 py-2">Code</th>
                  <th className="text-left px-5 py-2">Role</th>
                  <th className="text-left px-5 py-2">Expires</th>
                  <th className="text-left px-5 py-2">Last used</th>
                  <th className="text-left px-5 py-2">Status</th>
                  <th className="text-right px-5 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && !loading && (
                  <tr><td colSpan={8} className="px-5 py-4 text-[12.5px] text-[#7A857F]">No access codes.</td></tr>
                )}
                {visible.map((c) => (
                  <tr key={c.id} className="border-t border-[#F0E7D2]" data-testid={`code-row-${c.id}`}>
                    <td className="px-5 py-2.5 text-[#1A2622] font-medium">{c.email}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{c.name || '—'}{c.notes && <div className="text-[10.5px] text-[#7A857F]" data-testid={`code-notes-${c.id}`}>{c.notes}</div>}</td>
                    <td className="px-5 py-2.5">
                      <button onClick={() => copy(c.code)}
                        className="inline-flex items-center gap-1 font-mono text-[14px] text-[#1A2622] bg-[#F5EFDD] px-2 py-1 rounded hover:bg-[#E5DCC4]"
                        data-testid={`code-copy-${c.id}`}>
                        {c.code} <Copy size={11} />
                      </button>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] uppercase tracking-wider font-semibold border ${c.role === 'admin' ? 'bg-[#F5EFDD] text-[#A06628] border-[#D4C9AF]' : 'bg-[#E5F0EA] text-[#2D5F4F] border-[#4A7C5E]/30'}`}>{c.role}</span>
                    </td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{fmtDate(c.expires_at)}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">
                      {c.last_used_at ? <span>{fmtDate(c.last_used_at)} <span className="text-[11px] text-[#7A857F]">({c.use_count}×)</span></span> : <span className="text-[#7A857F]">never</span>}
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] uppercase tracking-wider font-semibold border ${c.revoked ? 'bg-[#F0E7D2] text-[#7A857F] border-[#D4C9AF]' : 'bg-[#E5F0EA] text-[#2D5F4F] border-[#4A7C5E]/30'}`}>
                        {c.revoked ? 'Revoked' : 'Active'}
                      </span>
                      <div className="mt-1 text-[10.5px] text-[#7A857F]" data-testid={`code-sub-${c.id}`}>
                        {c.role === 'admin' ? 'no payment needed' : (c.subscription_status ? `Paid: ${c.subscription_status}` : 'Not subscribed')}
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => regenerate(c.id)} className="inline-flex items-center gap-1 text-[12px] text-[#4A7C5E] hover:text-[#2D5F4F] px-1.5" data-testid={`code-regen-${c.id}`}>
                        <RotateCcw size={12} /> New
                      </button>
                      <button onClick={() => toggleRevoke(c)} className="inline-flex items-center gap-1 text-[12px] text-[#A06628] hover:text-[#7A2E0F] px-1.5 ml-1" data-testid={`code-revoke-${c.id}`}>
                        {c.revoked ? <><Check size={12} /> Restore</> : <><Ban size={12} /> Revoke</>}
                      </button>
                      <button onClick={() => remove(c)} className="inline-flex items-center gap-1 text-[12px] text-[#7A2E0F] hover:text-[#5A1C0A] px-1.5 ml-1" data-testid={`code-delete-${c.id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-[10.5px] text-[#A8B0AA] sm:hidden text-center">← Swipe to see all columns →</div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

const inputCls = 'mt-1 h-10 w-full px-2.5 rounded-md border border-[#E5DCC4] text-[13px] bg-white outline-none focus:border-[#4A7C5E]';

function Field({ label, required, children }) {
  return (
    <label className="block text-[11.5px] text-[#3A4A44]">
      <span className="uppercase tracking-[0.14em] font-semibold">
        {label} {required && <span className="text-[#A06628]">*</span>}
      </span>
      {children}
    </label>
  );
}
